/**
 * Compact workflow strip for solution pages.
 * Renders N steps horizontally on desktop with connecting arrows, wraps to
 * multi-row on mobile. Purely presentational - takes an already-shaped
 * `workflow: { eyebrow, heading, intro, steps: [{title}] }` object.
 */
import { motion } from "framer-motion";
import {
  ArrowRight,
  FileText, Pencil, CheckCircle2, Factory,
  ShieldCheck, Truck, Receipt, ClipboardList,
  Package, Users, Wrench, Cog,
} from "lucide-react";

// Name-mapped icons - fallback to FileText for anything else.
const ICONS = {
  "purchase order":   FileText,
  "purchase orders":  FileText,
  "po":               FileText,
  "artwork":          Pencil,
  "approval":         CheckCircle2,
  "approvals":        CheckCircle2,
  "proof":            Pencil,
  "production":       Factory,
  "quality control":  ShieldCheck,
  "qc":               ShieldCheck,
  "quality":          ShieldCheck,
  "shipping":         Truck,
  "invoice":          Receipt,
  "billing":          Receipt,
  "receiving":        Package,
  "planning":         Cog,
  "scheduling":       Cog,
  "customers":        Users,
  "orders":           ClipboardList,
  "assembly":         Wrench,
};

function iconFor(label) {
  return ICONS[label.trim().toLowerCase()] || FileText;
}

const COLORS = [
  { bg: "bg-sky-100/70",     ring: "ring-sky-200",     text: "text-sky-600"     },
  { bg: "bg-amber-100/70",   ring: "ring-amber-200",   text: "text-amber-600"   },
  { bg: "bg-emerald-100/70", ring: "ring-emerald-200", text: "text-emerald-600" },
  { bg: "bg-violet-100/70",  ring: "ring-violet-200",  text: "text-violet-600"  },
  { bg: "bg-rose-100/60",    ring: "ring-rose-200",    text: "text-rose-600"    },
  { bg: "bg-orange-100/70",  ring: "ring-orange-200",  text: "text-orange-600"  },
  { bg: "bg-indigo-100/70",  ring: "ring-indigo-200",  text: "text-indigo-600"  },
];

export default function SolutionWorkflow({ workflow }) {
  if (!workflow || !Array.isArray(workflow.steps) || workflow.steps.length === 0) return null;

  const steps = workflow.steps;

  return (
    <section
      data-testid="solution-workflow-section"
      className="py-12 lg:py-16 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {(workflow.eyebrow || workflow.heading || workflow.intro) && (
          <div className="text-center max-w-3xl mx-auto mb-10">
            {workflow.eyebrow && (
              <span
                data-testid="solution-workflow-eyebrow"
                className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600"
              >
                {workflow.eyebrow}
              </span>
            )}
            {workflow.heading && (
              <h2
                data-testid="solution-workflow-heading"
                className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight"
              >
                {workflow.heading}
              </h2>
            )}
            {workflow.intro && (
              <p className="mt-3 text-sm lg:text-base text-slate-500 leading-relaxed">
                {workflow.intro}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-stretch justify-center gap-2 sm:gap-3">
          {steps.map((step, i) => {
            const Icon = iconFor(step.title || "");
            const color = COLORS[i % COLORS.length];
            const isLast = i === steps.length - 1;
            const slug = (step.title || `step-${i}`)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return (
              <motion.div
                key={`${slug}-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.35 }}
                className="flex items-center gap-2 sm:gap-3"
              >
                <div
                  data-testid={`solution-workflow-step-${slug}`}
                  className="flex items-center gap-2 bg-white border border-slate-200 rounded-full pl-2 pr-4 py-1.5 shadow-sm"
                >
                  <span className={`w-7 h-7 rounded-full ${color.bg} ring-1 ${color.ring} flex items-center justify-center`}>
                    <Icon className={`h-3.5 w-3.5 ${color.text}`} strokeWidth={2.25} />
                  </span>
                  <span className="text-xs sm:text-sm font-semibold text-slate-800 whitespace-nowrap">
                    {step.title}
                  </span>
                </div>
                {!isLast && (
                  <ArrowRight className="hidden sm:inline h-4 w-4 text-sky-400 shrink-0" strokeWidth={2.25} aria-hidden />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
